import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { api, formatError } from "../../lib/api";
import { FilesField, ImageField } from "./UploadFields";

const base = "w-full rounded-md border border-input bg-white px-3 py-2 text-sm text-teal-ink outline-none focus:border-teal";

const FieldInput = ({ f, value, set, tid }) => {
  switch (f.type) {
    case "textarea":
      return <textarea rows={f.rows || 3} value={value || ""} onChange={(e) => set(e.target.value)} className={base} data-testid={tid} />;
    case "longtext":
      return <textarea rows={14} value={value || ""} onChange={(e) => set(e.target.value)} className={`${base} font-mono text-[13px] leading-relaxed`} data-testid={tid} />;
    case "select":
      return (
        <select value={value} onChange={(e) => set(e.target.value)} className={base} data-testid={tid}>
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    case "combo":
      return (
        <>
          <input list={`${tid}-list`} value={value || ""} onChange={(e) => set(e.target.value)} className={base} data-testid={tid} />
          <datalist id={`${tid}-list`}>{f.options.map((o) => <option key={o} value={o} />)}</datalist>
        </>
      );
    case "tags":
      return <input value={(value || []).join(", ")} onChange={(e) => set(e.target.value.split(",").map((s) => s.trimStart()))} onBlur={(e) => set(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} className={base} data-testid={tid} />;
    case "multi":
      return (
        <div className="flex flex-wrap gap-2" data-testid={tid}>
          {f.options.map((o) => {
            const on = (value || []).includes(o);
            return (
              <button type="button" key={o} onClick={() => set(on ? value.filter((x) => x !== o) : [...(value || []), o])} data-testid={`${tid}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`} className={`rounded-full border px-3 py-1.5 text-xs ${on ? "border-teal bg-teal text-mist" : "border-teal/30 text-teal"}`}>
                {o}
              </button>
            );
          })}
        </div>
      );
    case "number":
      return <input type="number" value={value ?? 0} onChange={(e) => set(Number(e.target.value))} className={`${base} max-w-[10rem]`} data-testid={tid} />;
    case "date":
      return <input type="date" value={value ? value.slice(0, 10) : ""} onChange={(e) => set(e.target.value ? new Date(e.target.value).toISOString() : null)} className={`${base} max-w-[14rem]`} data-testid={tid} />;
    case "switch":
      return <Switch checked={!!value} onCheckedChange={set} data-testid={tid} />;
    case "image":
      return <ImageField value={value} onChange={set} testId={tid} />;
    case "files":
      return <FilesField value={value} onChange={set} testId={tid} />;
    default:
      return <input required={f.required} value={value || ""} placeholder={f.placeholder} onChange={(e) => set(e.target.value)} className={base} data-testid={tid} />;
  }
};

export const EditorDialog = ({ open, onOpenChange, schemaKey, schema, item, onSaved }) => {
  const [form, setForm] = useState(schema.defaults);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(item ? { ...schema.defaults, ...item } : schema.defaults);
  }, [item, schema, open]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = Object.fromEntries(schema.fields.map((f) => [f.key, form[f.key]]));
    Object.keys(payload).forEach((k) => {
      if (Array.isArray(payload[k]) && typeof payload[k][0] === "string") payload[k] = payload[k].map((s) => s.trim()).filter(Boolean);
    });
    try {
      if (item?.id) await api.put(`/admin/${schemaKey}/${item.id}`, payload);
      else await api.post(`/admin/${schemaKey}`, payload);
      toast.success(`${schema.singular[0].toUpperCase()}${schema.singular.slice(1)} saved`);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto bg-paper" data-testid="editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-normal text-teal">{item?.id ? `Edit ${schema.singular}` : `New ${schema.singular}`}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-6">
          {schema.fields.map((f) => (
            <div key={f.key} className={f.type === "switch" ? "flex items-center justify-between rounded-md border border-input bg-white px-4 py-3" : ""}>
              <label className="mb-2 block text-xs font-medium uppercase tracking-[0.12em] text-teal/80">{f.label}</label>
              <FieldInput f={f} value={form[f.key]} set={(v) => setForm((s) => ({ ...s, [f.key]: v }))} tid={`field-${f.key}`} />
              {f.hint && <p className="mt-2 text-xs text-teal/70">{f.hint}</p>}
            </div>
          ))}
          <div className="sticky -bottom-6 -mx-6 flex justify-end gap-3 border-t border-teal/10 bg-paper px-6 py-4 pb-6">
            <button type="button" onClick={() => onOpenChange(false)} className="rounded-full border border-teal/30 px-5 py-2.5 text-sm text-teal" data-testid="editor-cancel-btn">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-teal px-6 py-2.5 text-sm text-mist hover:bg-teal-ink disabled:opacity-60" data-testid="editor-save-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
